import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { CommonService } from '../../../services/common/common.service';
import { ToolsService } from '../../../services/tools/tools.service';
import { Column } from '../../interfaces/column.interface';
import { UsersDataService, UsersColumnsService } from '../../services';
import { DialogUserFormComponent } from './user-form/user-form.component';
import { DialogUserDetailsComponent } from './user-details/user-details.component';
import { User, UsersResult } from '../../interfaces';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss'],
  providers: [MessageService, ConfirmationService, DialogService],
})
export class UsersComponent implements OnInit, OnDestroy {

  private userAddDialog: DynamicDialogRef = new DynamicDialogRef();
  private userEditDialog: DynamicDialogRef = new DynamicDialogRef();
  private userDetailsDialog: DynamicDialogRef = new DynamicDialogRef();
  public userToDelete: User = {};
  public users: User[] = [];
  public user: User = {};
  public selectedUsers: User[] = [];
  public submitted: boolean = false;
  public parameters: {[param: string]: string | string[]} = {offset: '0', limit: '100'};
  public chunkSize: number = 200;
  public columns: Column[] = [];
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public locale: string = (localStorage.language)?localStorage.language:'en';
  private _selectedColumns: Column[] = [];
  @ViewChild('importer', { static: false })
  public importer?: FileUpload;
  public importChooseLabel: string = 'Import';
  public importLabel: string = 'Import CSV';

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private common: CommonService,
    private router: Router,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private usersDataService: UsersDataService,
    private usersColumnsService: UsersColumnsService,
  ) {
    this.importChooseLabel = this.translate.instant('IMPORT');
    this.importLabel = this.translate.instant('IMPORTCSV');
  }

  ngOnInit(): void {
    console.log('UsersComponent', 'ngOnInit');
    this.isInitialized = true;
    this.titleService.setTitle('Users - Management');
    this.primengConfig.ripple = true;
    this.getUsers();
  }

  ngOnDestroy(): void {
    console.log('UsersComponent', 'ngOnDestroy');
    this.isInitialized = false;
  }

  getUsers(): void {
    if (!this.isInitialized) {
      return;
    }
    if (parseInt(this.parameters.offset + '', 10) === 0) {
      this.isLoading = true;
    }
    this.usersDataService.getUsers(this.parameters).subscribe((response: UsersResult|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.setColumns();
        if (parseInt(this.parameters.offset + '', 10) === 0) {
          this.users = response.results;
        } else {
          this.users = this.users.concat(response.results);
        }
        if (response.inlineCount > this.users.length) {
          this.parameters.offset = (parseInt(this.parameters.offset + '', 10) + parseInt(this.parameters.limit + '', 10)) + '';
          this.getUsers();
        } else {
          this.parameters.offset = '0';
        }
      }
    }, (error: any) => {;
      this.isLoading = false;
      this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('USERSNOTLOADED')});
    });
  }

  importUsers(event: any): void {
    if (typeof event.currentFiles === 'undefined') {
      return;
    }
    if (event.currentFiles.length === 0) {
      return;
    }
    const inputFile = event.currentFiles[0];
    const reader = new FileReader();
    let rows: any[] = [];
    reader.onload = (e: any) => {
      if (typeof this.importer !== 'undefined') {
        this.importer.clear();
      }
      const text = <string>e.target.result;
      rows = ToolsService.csvToArray(this.columns, text, ',');
      if (rows.length > 0) {
        let proceed = 0;
        const j = rows.length;
        for (let i = 0; i < j; i += this.chunkSize) {
          this.isLoading = true;
          const chunk = rows.slice(i, i + this.chunkSize);
          if (typeof chunk[0].id === 'undefined') {
            this.usersDataService.addUsers(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('USERSCREATED')});
                if (proceed === j) {
                  this.getUsers();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('USERSNOTCREATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('USERSNOTCREATED') + ' ' + msg});
            });
          } else {
            this.usersDataService.updateUsers(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('USERSUPDATED')});
                if (proceed === j) {
                  this.getUsers();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('USERSNOTUPDATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('USERSNOTCREATED') + ' ' + msg});
            });
          }
        }
      }
    };
    reader.readAsText(inputFile);
  }

  setColumns() {
    if (this.columns.length > 0) {
      return;
    }
    this.columns = this.usersColumnsService.getColumns();
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].show) {
        this._selectedColumns.push(this.columns[i]);
      }
    }
  }

  @Input()
  get selectedColumns(): any[] {
      return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
      // restore original order
      this._selectedColumns = this.columns.filter((col: any) => val.includes(col));
      this.updateColumns();
  }

  updateColumns() {
    for (let i = 0; i < this.columns.length; i++) {
      const show = this._selectedColumns.includes(this.columns[i]);
      this.columns[i].show = show;
      this.navigation.setParamValue('users.' + this.columns[i].field + '.showFiled', show);
    }
  }

  deleteSelectedUsers() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the selected Users?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.usersDataService.deleteUsers(this.selectedUsers).subscribe((response) => {
          if (response === null) {
            this.messageService.clear();
            this.messageService.add({severity:'success', summary: 'Successful', detail: 'Users Deleted', life: 3000});
            this.getUsers();
          } else {
            this.messageService.clear();
            this.messageService.add({severity: 'error', summary: 'Error', detail: 'Users not deleted'});
          }
        }, (error: any) => {
          this.messageService.clear();
          this.messageService.add({severity: 'error', summary: 'Error', detail: 'Users not deleted'});
        });
        this.selectedUsers = [];
      }
    });
  }

  exportPdf() {
    ToolsService.exportPdf(this.users, this.columns, 'Users');
  }

  exportExcel() {
    ToolsService.exportExcel(this.users, this.columns, 'Users');
  }

  exportCsv() {
    ToolsService.exportCsv(this.users, this.columns, 'Users');
  }

  refresh() {
    this.usersDataService.clearCache();
    this.getUsers();
  }

  userAdd(): void {
    if (!this.navigation.getParamValue('users.addInDialog', true)) {
      this.router.navigate(['/users/add']);
      return;
    }
    this.userAddDialog = this.dialogService.open(DialogUserFormComponent, {
      header: '', // this.translate.instant('NEWUSER'),
      width: '80%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'add',
        user: {id: null},
        companies: this.usersColumnsService.companies,
        countries: this.usersColumnsService.countries,
        cities: this.usersColumnsService.cities,
        languages: this.usersColumnsService.languages,
      },
    });

    this.userAddDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'info', summary: 'Success', detail: 'User added'});
        this.getUsers();
      }
    });
  }

  userEdit(user: User): void {
    if (!this.navigation.getParamValue('users.editInDialog', true)) {
      this.router.navigate(['/users/edit/' + user.id]);
      return;
    }
    this.userEditDialog = this.dialogService.open(DialogUserFormComponent, {
      header: '', // this.translate.instant('EDITUSER') + ' ' + user.username,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'edit',
        user: user,
        companies: this.usersColumnsService.companies,
        countries: this.usersColumnsService.countries,
        cities: this.usersColumnsService.cities,
        languages: this.usersColumnsService.languages,
      },
    });

    this.userEditDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'success', summary: 'Success', detail: 'User updated'});
        this.getUsers();
      }
    });
  }

  userDetails(user: User): void {
    if (!this.navigation.getParamValue('users.detailsInDialog', true)) {
      this.router.navigate(['/users/details/' + user.id]);
      return;
    }
    this.userDetailsDialog = this.dialogService.open(DialogUserDetailsComponent, {
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        user: user,
      }
    });
  }

  userDelete(user: User): void {
    this.userToDelete = user;
    this.messageService.clear();
    this.messageService.add({
      key: 'userDelete',
      sticky: true,
      severity: 'warn',
      summary: 'Are you sure you want delete ' + user.username + ' ?',
      detail: 'Confirm to proceed'
    });
  }

  onDeleteConfirm(): void {
    this.usersDataService.deleteUser(this.userToDelete).subscribe((res: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'success', summary: 'Success', detail: 'User deleted'});
      this.getUsers();
    }, (error: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'error', summary: 'Error', detail: 'User not deleted'});
    });
  }

  onDeleteReject(): void {
    this.userToDelete = {};
    this.messageService.clear('userDelete');
  }

}
